'use client'

import { useParams, useRouter } from 'next/navigation'
import { union } from 'ramda'
import { useEffect, useState } from 'react'

import { geoBeanchLocation } from '@/libs/haversine'

import type { Branch } from '../../destination/types/schema'

interface CityLink {
  name: string
  slug: string
}

interface AutoCityRedirectProps {
  cityLinks: CityLink[]
  branchData: any[]
  locale: string
}

export default function AutoCityRedirect({ cityLinks, branchData, locale }: AutoCityRedirectProps) {
  const router = useRouter()
  const [locationChecked, setLocationChecked] = useState(false)

  // Helper function to check if session storage item is expired
  const isSessionExpired = (key: string, expirationMinutes: number = 30): boolean => {
    const item = sessionStorage.getItem(key)
    if (!item) return true

    try {
      const data = JSON.parse(item)
      const now = Date.now()
      const expirationTime = data.timestamp + expirationMinutes * 60 * 1000

      if (now > expirationTime) {
        sessionStorage.removeItem(key)
        return true
      }
      return false
    } catch {
      sessionStorage.removeItem(key)
      return true
    }
  }

  // Helper function to set session storage with timestamp
  const setSessionWithExpiration = (key: string, value: string): void => {
    const data = {
      value,
      timestamp: Date.now()
    }
    sessionStorage.setItem(key, JSON.stringify(data))
  }

  useEffect(() => {
    async function detectAndRedirectToNearestCity() {
      try {
        //__Don't run if already checked or no branch data
        if (locationChecked || !branchData?.length) return

        //__Check if user has already been auto-redirected in this session (with 30 min expiration)
        const hasBeenRedirected = !isSessionExpired('promotion-auto-redirected', 30)
        if (hasBeenRedirected) {
          setLocationChecked(true)
          return
        }

        //__Check if user explicitly wants to see all promotions (with 60 min expiration)
        const userWantsAll = !isSessionExpired('user-wants-all-promotions', 60)
        if (userWantsAll) {
          setLocationChecked(true)
          return
        }

        //__Check if user is coming from a promotion city page (intentional navigation back)
        const referrer = document.referrer
        if (
          referrer &&
          referrer.includes(`/${locale}/promotion/`) &&
          !referrer.endsWith(`/${locale}/promotion`)
        ) {
          //__User is navigating back from a city page, don't redirect
          setLocationChecked(true)
          return
        }

        const allBranches = branchData
          .map((cityData) => cityData.branches || [])
          //__Get all branches from branch data
          .reduce<Branch[]>(union, [])

        if (!allBranches.length) return

        //__Use geolocation to find nearest branch
        const nearestBranch = await geoBeanchLocation(allBranches)

        if (nearestBranch) {
          //__Find the corresponding city link using city_code as fallback
          const citySlug = (nearestBranch as any).city_slug || nearestBranch.city_code
          const targetCity = cityLinks.find((city) => city.slug === citySlug)

          if (targetCity && targetCity.slug) {
            const targetUrl = `/${locale}/promotion/${targetCity.slug}`

            //__Mark that user has been auto-redirected in this session with timestamp
            setSessionWithExpiration('promotion-auto-redirected', 'true')
            //__Redirect to the nearest city's promotion page
            router.push(targetUrl)
          }
        }
      } catch (error) {
        //__Silently fail if geolocation is denied or fails
        // console.log('Geolocation detection failed, staying on main promotion page')
      } finally {
        setLocationChecked(true)
      }
    }

    //__Only run once when component mounts
    detectAndRedirectToNearestCity()
  }, [cityLinks, branchData, locale, router, locationChecked])

  //__Clear the auto-redirect flag when user navigates away from promotion pages
  useEffect(() => {
    const handleBeforeUnload = () => {
      //__Check if navigating away from promotion section entirely
      if (!window.location.pathname.includes('/promotion')) {
        sessionStorage.removeItem('promotion-auto-redirected')
        sessionStorage.removeItem('user-wants-all-promotions')
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  //__This component doesn't render anything visible
  return null
}
